import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  IResourceStatusBadge,
  IResourceValueRecord,
  StatusBadgeVariant,
} from './types'

const ACTIVITY_STATES = new Set(['in_use_moving', 'in_use_idle', 'inactive'])

const SIGNAL_STATUSES = new Set(['fresh', 'no_signal'])

const toRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null

const toString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

export const resolveEffectiveSensorState = (
  data: unknown,
): string | undefined => {
  const record = toRecord(data)
  if (!record) {
    return undefined
  }

  const signalStatus = toString(record.signal_status)
  if (signalStatus && SIGNAL_STATUSES.has(signalStatus)) {
    if (signalStatus === 'no_signal') {
      return 'no_signal'
    }
  }

  const activityState = toString(record.activity_state)
  if (activityState && ACTIVITY_STATES.has(activityState)) {
    return activityState
  }

  return undefined
}

export const resolveEquipmentSensorState = (
  record: IResourceValueRecord,
): string | undefined => {
  const root = toRecord(record.root)
  const linkedSensor = toRecord(root?.linkedSensor)
  return resolveEffectiveSensorState(linkedSensor?.data)
}

export const resolveStruxTagSensorState = (
  record: IResourceValueRecord,
): string | undefined => resolveEffectiveSensorState(record.data)

export const getSensorStateLabel = (value: unknown): string => {
  if (typeof value !== 'string') {
    return Localization.translator.unknown
  }

  switch (value) {
    case 'in_use_moving':
      return Localization.translator.sensorStateInUseMoving
    case 'in_use_idle':
      return Localization.translator.sensorStateInUseIdle
    case 'inactive':
      return Localization.translator.inactive
    case 'no_signal':
      return Localization.translator.sensorStateNoSignal
    default:
      return Localization.translator.unknown
  }
}

export type SensorStateBadgeVariant = StatusBadgeVariant

export const getSensorStateBadgeVariant = (
  value: unknown,
): SensorStateBadgeVariant => {
  switch (value) {
    case 'in_use_moving':
      return 'success'
    case 'in_use_idle':
      return 'caution'
    case 'no_signal':
      return 'destructive'
    case 'inactive':
    default:
      return 'muted'
  }
}

export const resolveSensorStateStatusBadge = (
  value: unknown,
): IResourceStatusBadge | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  return {
    variant: getSensorStateBadgeVariant(value),
    text: getSensorStateLabel(value),
  }
}
